import React, { useEffect, useState } from "react";
import { ChartContainer } from "../view/ChartContainer";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CustomTooltip } from "./components/CustomTooltip";
import { transformBarChartData } from "./utils/chartDataTransformers";
import { MSB_VIOLATION_COLOR, DEVICE_COLORS_10 } from "./constants/chartColors";
import * as statisticService from "./statisticService";
import { AuditScope } from "./chartDynamicLabels";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { InteractiveLegend } from "./components/InteractiveLegend";
import DashboardChartSkeleton from "./components/DashboardChartSkeleton";

function MsbViolationsChart({ fromDate, toDate, scope = AuditScope.BY_DAY }) {



  const [rechartsData, setRechartsData] = useState([]);
  const [barConfigs, setBarConfigs] = useState([]);
  const [overallData, setOverallData] = useState(null);
  const [viewMode, setViewMode] = useState('OVERALL'); // 'OVERALL' or 'BREAKDOWN'
  const [hiddenSeries, setHiddenSeries] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const mbData = await statisticService.getDashboardMsbViolations(scope);
      if (mbData && mbData.labels && mbData.datasets) {

        setOverallData({ ...mbData });
        setViewMode('OVERALL');

        // Transform for Recharts
        const transformed = transformBarChartData(mbData);
        setRechartsData(transformed);

        // Create bar configs - always use modern color
        const configs = mbData.datasets.map(dataset => ({
          dataKey: dataset.label.toLowerCase().replace(/\s+/g, '_'),
          name: dataset.label,
          fill: MSB_VIOLATION_COLOR  // Always use modern danger red (#dc2626)
        }));
        setBarConfigs(configs);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scope !== AuditScope.CUSTOM)
      fetchData();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [scope])

  const fetchDataCustom = async () => {
    setIsLoading(true);
    try {
      const mbData = await statisticService.getDashboardMsbViolationsCustom(fromDate, toDate);
      if (mbData && mbData.labels && mbData.datasets) {

        setOverallData({ ...mbData });
        setViewMode('OVERALL');

        // Transform for Recharts
        const transformed = transformBarChartData(mbData);
        setRechartsData(transformed);

        // Create bar configs - always use modern color
        const configs = mbData.datasets.map(dataset => ({
          dataKey: dataset.label.toLowerCase().replace(/\s+/g, '_'),
          name: dataset.label,
          fill: MSB_VIOLATION_COLOR  // Always use modern danger red (#dc2626)
        }));
        setBarConfigs(configs);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scope !== AuditScope.CUSTOM) {
      return;
    }

    const fromNotToday = isDateDifferentThanToday(fromDate);
    const toNotToday = isDateDifferentThanToday(toDate);
    checkCustomDatesSelected(fromNotToday, toNotToday);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [fromDate, toDate, scope])

  const checkCustomDatesSelected = (isFromDate, isToDate) => {
    if (isFromDate === true || isToDate === true) {
      fetchDataCustom();
    }
  }

  const isDateDifferentThanToday = (dateToCheck) => {
    if (!dateToCheck) {
      return false;
    }
    const today = new Date();
    const isSameDay =
      dateToCheck.getFullYear() === today.getFullYear() &&
      dateToCheck.getMonth() === today.getMonth() &&
      dateToCheck.getDate() === today.getDate();

    return !isSameDay;
  };

  // Handle legend click to toggle series visibility
  const handleToggleSeries = (dataKey) => {
    setHiddenSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  // Handle bar click to show device-level violations breakdown
  const handleBarClick = async (data, dataKey) => {
    // Prevent drill-down if already in breakdown mode
    if (viewMode === 'BREAKDOWN') return;

    // Only allow clicking on the "Violations" bar
    if (dataKey !== 'violations') return;

    let effectiveScope = scope;
    if (scope === AuditScope.CUSTOM && (isDateDifferentThanToday(fromDate) || isDateDifferentThanToday(toDate))) {
      effectiveScope = AuditScope.CUSTOM;
    }

    try {
      const deviceTrends = await statisticService.getMsbViolationsDeviceTrends(effectiveScope, fromDate, toDate);

      if (deviceTrends && deviceTrends.length > 0) {
        const sortedTrends = deviceTrends.map(device => {
          const sum = device.data.reduce((a, b) => a + b, 0);
          const avg = sum / device.data.length;
          return { ...device, averageViolations: avg };
        }).sort((a, b) => b.averageViolations - a.averageViolations);

        const top10Trends = sortedTrends.slice(0, 10);

        // Build Recharts data format with all devices
        const labels = overallData.labels;
        const transformedData = labels.map((label, idx) => {
          const point = { label };
          top10Trends.forEach((device) => {
            const deviceKey = device.deviceName.toLowerCase().replace(/\s+/g, '_');
            point[deviceKey] = device.data[idx];
          });
          return point;
        });

        // Create bar configs for devices
        const deviceConfigs = top10Trends.map((device, index) => ({
          dataKey: device.deviceName.toLowerCase().replace(/\s+/g, '_'),
          name: device.deviceName,
          fill: DEVICE_COLORS_10[index % DEVICE_COLORS_10.length]
        }));

        setRechartsData(transformedData);
        setBarConfigs(deviceConfigs);
        setViewMode('BREAKDOWN');
      }
    } catch (error) {
      console.error('Error fetching device trends:', error);
    }
  };

  const handleBackClick = () => {
    if (overallData) {
      // Transform back to overall view
      const transformed = transformBarChartData(overallData);
      setRechartsData(transformed);

      // Restore overall configs with modern color
      const configs = overallData.datasets.map(dataset => ({
        dataKey: dataset.label.toLowerCase().replace(/\s+/g, '_'),
        name: dataset.label,
        fill: MSB_VIOLATION_COLOR  // Modern danger red
      }));
      setBarConfigs(configs);
      setViewMode('OVERALL');
    }
  };

  if (isLoading) {
    return <DashboardChartSkeleton title="Average Baseline Rules Violations" subtitle="Violations" />;
  }

  return (
    <ChartContainer noHeader={true} className="oct_chart-card-v2">
      <div className="oct_chart-header-with-action-v2">
        <div className="oct_chart-info-v2">
          <p className="oct_chart-subtitle-v2">Violations</p>
          <h3 className="oct_chart-title-v2">Average Baseline Rules Violations</h3>
        </div>
        {viewMode === 'BREAKDOWN' && (
          <button
            onClick={handleBackClick}
            className="oct_chart-back-btn-v2"
          >
            <ArrowBackIcon className="back-arrow-v2" />
            <span>Go Back</span>
          </button>
        )}
      </div>
      <div className="oct_interactive-legend-wrapper-v2">
        {barConfigs.length > 0 && (
          <InteractiveLegend
            payload={barConfigs.map(config => ({
              value: config.name,
              color: config.fill,
              dataKey: config.dataKey
            }))}
            hiddenSeries={hiddenSeries}
            onToggleSeries={handleToggleSeries}
          />
        )}
      </div>
      <div className="oct_recharts-wrapper-v2">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 10, height: 10 }}>
          <BarChart
            data={rechartsData}
            barSize={viewMode === 'BREAKDOWN' ? 20 : 40}
            barCategoryGap={viewMode === 'BREAKDOWN' ? '10%' : '20%'}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
            />
            <YAxis
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            {barConfigs.map((config) => (
              <Bar
                key={config.dataKey}
                dataKey={config.dataKey}
                name={config.name}
                fill={config.fill}
                radius={[8, 8, 0, 0]}
                hide={hiddenSeries[config.dataKey]}
                onClick={(data) => handleBarClick(data, config.dataKey)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartContainer>
  )
}

export default MsbViolationsChart;
